// Utils
import { consoleWarn } from 'app/shared/utils/console';
import { matchPath } from 'react-router-dom';
import getFromProcessEnv from 'app/shared/utils/getFromProcessEnv';
import permissions, { Permission } from 'app/shared/utils/permissions';
import { FeatureFlag } from 'app/shared/featureFlags/models';

// combine path strings without "//" bugs
// https://stackoverflow.com/questions/29855098/is-there-a-built-in-javascript-function-similar-to-os-path-join
export const pathJoin = (...args: Array<string | number>) => {
  return args
    .map((part, i) => {
      if (i === 0) {
        return String(part).trim().replace(/[/]*$/g, '');
      }
      return String(part)
        .trim()
        .replace(/(^[/]*|[/]*$)/g, '');
    })
    .filter((x) => x.length)
    .join('/');
};

export const openInNewTab = (path: string) => {
  const baseUrl = getFromProcessEnv('REACT_APP_AUTH0_LOGIN_REDIRECT_URL');
  // add base url to our shortened /example routes if not coming from a test
  const formattedPath =
    process.env.NODE_ENV !== 'test' ? pathJoin(baseUrl, path) : path;

  // temporarily remove routing history since window.open transfers it to the new tab
  // Note: cannot use imports from sessionStorage since there's an import cycle
  const history = sessionStorage.getItem('ROUTING_HISTORY');
  sessionStorage.removeItem('ROUTING_HISTORY');
  window.open(formattedPath);
  if (history) {
    sessionStorage.setItem('ROUTING_HISTORY', history);
  }
};

/**
 * @deprecated Use ROUTES or ROUTES_MAP for lumos routes instead
 * Define patronus routes directly in react-query hook instead
 */
const routes = {
  lumos: {
    // Home
    home: '/',

    // Unit21 Login
    agentLogin: '/agent-login',
    emailLogin: '/email-login',

    // Cases Routes
    cases: '/cases',
    casesId: '/cases/:id',
    casesIdTab: '/cases/:id/:tab?',
    casesIdAttachments: '/cases/:id/attachments',
    casesIdComments: '/cases/:id/comments',
    casesCreate: '/cases/create',
    pdfExportCasesId: '/pdf-export/cases/:caseId',

    // Dashboards Routes
    dashboards: '/dashboards',
    dashboardsId: '/dashboards/:id',

    // Agents Routes
    agentsId: '/agents/:id',
    agentsIdTab: '/agents/:id/:tab?',

    // Teams Routes
    teamsId: '/teams/:id',
    teamsIdTab: '/teams/:id/:tab?',

    // Alerts Routes
    alerts: '/alerts',
    alertsCreate: '/alerts/create',
    myAlerts: '/alerts/my-alerts',
    alertAdmin: '/alerts/admin',
    alertsId: '/alerts/:id',
    alertsIdTab: '/alerts/:id/:tab?',
    pdfExportAlertsAlertId: '/pdf-export/alerts/:alertId',

    // Entities Routes
    entitiesId: '/entities/:id',
    entitiesExternalId: '/unit21/entities/:entity-external-id',
    entitiesIdTab: '/entities/:id/:tab?',

    // Report filings
    filings: '/filings',

    // Report filings: SAR
    filingsSARs: '/filings/sars',
    filingsSar: '/filings/sar',
    filingsSarId: '/filings/sar/:id',

    // Report filings: goAML
    filingsGoAMLCreate: '/filings/go-aml/create',
    filingsGoAMLIdEdit: '/filings/go-aml/:id/edit',
    filingsGoAMLIdEditTab: '/filings/go-aml/:id/edit/:tab?',

    // Txn Instruments Routes
    instruments: '/instruments',
    instrumentsId: '/instruments/:id',
    instrumentsIdTab: '/instruments/:id/:tab?',

    // Notifications Routes
    notifications: '/notifications',

    // Error Routes
    error: '/error',
    fourOhfour: '/404',
    externalIdDetected: '/invalid-id-detected',

    // Webhook Routes
    webhooksId: '/webhooks/:id',

    // Data Explorer
    dataExplorer: '/data-explorer',
    dataExplorerEntities: '/data-explorer/entities',
    dataExplorerUploadFile: '/data-explorer/upload-file',
    dataExplorerInstruments: '/data-explorer/instruments',
    dataExplorerTransactions: '/data-explorer/transactions',
    dataExplorerActionEvents: '/data-explorer/action-events',
    dataExplorerActionEventsId: '/data-explorer/action-events/:id',
    dataExplorerTransactionsId: '/data-explorer/transactions/:id',
    dataExplorerDataMappingUI: '/data-explorer/data-mapping/:id',
    dataExplorerFileUploads: '/data-explorer/flat-file-ingestion',
  },
  patronus: {
    // Orgs
    orgsScenarioConfigs: '/v1/orgs/scenario-configs',
    orgsAPIConfig: '/v1/orgs/api-config',
    orgsRelevantOrgsDetails: '/v1/orgs/relevant-orgs/details',

    // Permissions
    permissionsAssociationTypeList:
      '/v1/permissions/:associationType/:associationId/assigned',
    permissionsAssociationTypeUnassigned:
      '/v1/permissions/:associationType/:associationId/unassigned',
    permissionsAssociationTypeAssociationId:
      '/v1/permissions/:associationType/:associationId',

    apiKeysList: '/v1/api-keys/list',
    apiKeysCreate: '/v1/api-keys/create',
    apiKeysUpdatePermissions: '/v1/api-keys/update-permissions',
    apiKeysDeactivateKeyPrefix: '/v1/api-keys/deactivate/:keyPrefix',

    webhooksActionTriggers: '/v1/webhooks/action-triggers',

    goAMLTemplatesUpdate: '/v1/go-aml/templates/update',
    goAMLTemplates: '/v1/go-aml/templates/list',

    // Cases API
    casesCreate: '/v1/cases/create',
    casesEdit: '/v1/cases/edit/',
    casesEditId: '/v1/cases/edit/:id',
    casesIdAttachmentsAttachmentId: '/v1/cases/:id/attachments/:attachmentId',
    casesIdAddFiles: '/v1/cases/:id/add-files',
    casesBulkXlsx: '/v1/cases/bulk-xlsx',

    // Comments API
    comments: '/v1/comments',

    // Entities API
    entitiesIdPaginatedAlerts:
      '/v1/entities/:entity-external-id/paginated-alerts',
    entitiesEditId: '/v1/entities/edit/:id',
    entitiesPaginatedGloballyWhitelistedRules:
      '/v1/entities/paginated-globally-whitelisted-entities',
    entitiesWhitelistGlobally: '/v1/entities/whitelist-globally',
    entitiesUnwhitelistGlobally: '/v1/entities/unwhitelist-globally',

    // Custom Checklist API
    customChecklist: '/v1/custom-checklist',
    customChecklistSearch: '/v1/custom-checklist/search',
    customChecklistId: '/v1/custom-checklist/:id',
    customChecklistArticleIdSubmission:
      '/v1/custom-checklist/article/:id/submission',

    // Investigation Checklist
    investigationChecklistList: '/v1/custom-checklist/list',

    // Events API
    eventsEventId: '/v1/events/:eventId',

    // Events
    txnEventsEvaluationLogEventId: '/v1/txn-events/:eventId/evaluation_log',

    // Uploads API
    uploadsDatafiles: '/v1/uploads/datafiles',
    uploadsDatafilesList: '/v1/uploads/datafiles/list',
    uploadsDatafilesDownload: '/v1/uploads/datafiles/download',
    uploadsDatafilesDelete: '/v1/uploads/datafiles/delete',
    uploadsDatafilesProcess: '/v1/uploads/datafiles/:id/process',

    // Tags API
    tagsAdd: '/v1/tags/add',
    tagsList: '/v1/tags/list',

    // Rules API
    rulesList: '/v1/rules/list',
    rulesBulkDeactivate: '/v1/rules/bulk-deactivate',
    rulesWhitelistEntities: '/v1/rules/whitelist-entities',
    validationRulesIdDelete: `/v1/validation-rules/:id/delete`,
    ruleValidationAlertsIdEntities: '/v1/rule-validation-alerts/:id/entities',
    shadowRulesIdDeploy: `/v1/shadow-rules/:id/deploy`,
    rulesEditId: '/v1/rules/edit/:id',

    // Rules
    detectionModels: '/v1/detection-models',
    detectionModelsPerformanceChart: '/v1/detection-models/performance-chart',
    detectionModelsValidationId: '/v1/detection-models/validation/:id',
    detectionModelsValidationIdMetrics:
      '/v1/detection-models/validation/:id/metrics',
    detectionModelsValidate: '/v1/detection-models/validate',
    detectionModelsRevalidateId: '/v1/detection-models/revalidate/:id',
    detectionModelsIdActivate: '/v1/detection-models/:id/activate',
    detectionModelId: '/v1/detection-models/:id',
    detectionModelsTemplates: '/v1/detection-models/templates/list',
    detectionModelsTest: '/v1/detection-models/:id/test',

    // Alerts API
    alertsAlertId: '/v1/alerts/:alertId',
    alertsIdTransactions: '/v1/alerts/:id/transactions',
    alertsIdActionEvents: '/v1/alerts/:id/action-events',
    alertsIdEntities: '/v1/alerts/:id/entities',
    alertsIdInstruments: '/v1/alerts/:id/instruments',
    alertsCreate: '/v1/alerts/create',
    alertsIdComponentResolution: '/v1/alerts/:id/component-resolution',
    alertsEditId: '/v1/alerts/edit/:id',
    alertsIdAddFiles: '/v1/alerts/:id/add-files',
    alertsIdAttachmentId: '/v1/alerts/:id/attachments/:attachmentId',
    alertsBulkXlsx: '/v1/alerts/bulk-xlsx',

    // Auth API
    agentsIdp: '/v1/agents/idp', // get google IDP
    googleOauthRefresh: '/v1/google-oauth/refresh',
    login: '/v1/login',
    tokenPing: '/v1/token-ping',
    logout: '/v1/logout',

    // Agents API
    agentsAutocomplete: '/v1/agents/autocomplete',
    agentsExchangeAuthEmailToken: '/v1/agents/exchange-auth-email-token',
    agentsRefreshJwt: '/v1/agents/refresh-jwt',

    // Teams API
    teamsSearch: '/v1/teams/search',

    // Sars API
    sarsCreate: '/v1/sars/create',
    sars: '/v1/sars',
    sarsId: '/v1/sars/:id',
    sarsList: '/v1/sars/list',
    sarsArchive: '/v1/sars/archive',

    // GoAML API
    goAMLCreate: '/v1/go-aml/create',
    goAMLCreateCached: '/v1/go-aml/cached/create',
    goAMLIdEdit: '/v1/go-aml/:id/edit',
    goAMLIdExport: '/v1/go-aml/:id/export',
    goAMLValidate: '/v1/go-aml/:id/validate',
    goAMLIdAttachmentAttachmentId: '/v1/go-aml/:id/attachment/:attachmentId',
    goAMLIdAttachment: '/v1/go-aml/:id/attachment',

    // Custom/Table Configs API
    customConfigs: '/v1/custom-configs',
    customConfigsOrg: '/v1/custom-configs/org',

    // Workflows
    workflows: '/v1/workflows',
    workflowsId: '/v1/workflows/:id',
    workflowsList: '/v1/workflows/list',
    workflowsSubdispositionsList: '/v1/workflows/subdispositions',

    // Notifications API
    notificationsPaginated: '/v1/notifications/paginated',
    notificationsRead: '/v1/notifications/read',
    notificationsReadAll: '/v1/notifications/read-all',

    // Blacklists API
    blacklistsList: '/v1/blacklists/list',
    blacklistsCreate: '/v1/blacklists/create',

    // File Exports API
    fileExportsDownload: '/v1/file-exports/download',
  },

  external: {
    // External (interwebs)
    googleDrive: {
      filePrefix: 'https://drive.google.com/file/d/',
      folderPrefix: 'https://drive.google.com/drive/folders/',
    },
  },
};

export interface Route {
  exact?: boolean;
  id: string;
  name: string;
  path: string;
  permissions?: Permission[];
  anyPermissions?: Permission[];
  requiredFeatureFlags?: FeatureFlag[];
}

// this will be the new way to define routes
// additional keys (anything used in AppRouter) will be added
//
// Exact routes should come before non-exact routes if the non-exact route is part of the exact route path
export const ROUTES = [
  // Alerts
  { exact: true, id: 'alerts', name: 'Alerts', path: routes.lumos.alerts },
  {
    exact: true,
    id: 'myAlerts',
    name: 'My Alerts',
    path: routes.lumos.myAlerts,
    permissions: [permissions.readAlerts],
  },
  {
    exact: true,
    id: 'myAlertsEntities',
    name: 'My Alerts by Entities',
    path: '/alerts/my-alerts/entities',
    permissions: [permissions.readAlerts, permissions.readEntities],
  },
  {
    exact: true,
    id: 'myAlertsInstruments',
    name: 'My Alerts by Instruments',
    path: '/alerts/my-alerts/instruments',
    permissions: [permissions.readAlerts, permissions.readTxnInstruments],
  },

  {
    exact: true,
    id: 'alertAdmin',
    name: 'Admin Alerts',
    path: routes.lumos.alertAdmin,
    permissions: [permissions.readAlerts, permissions.readAssignments],
  },
  {
    exact: true,
    id: 'alertAdminEntities',
    name: 'Admin Alerts by Entities',
    path: '/alerts/admin/entities',
    permissions: [
      permissions.readAlerts,
      permissions.readAssignments,
      permissions.readEntities,
    ],
  },
  {
    exact: true,
    id: 'alertAdminInstruments',
    name: 'Admin Alerts by Instruments',
    path: '/alerts/admin/instruments',
    permissions: [
      permissions.readAlerts,
      permissions.readAssignments,
      permissions.readTxnInstruments,
    ],
  },
  {
    exact: true,
    id: 'queuedAlerts',
    name: 'Queued Alerts',
    path: '/alerts/queued-alerts',
    permissions: [permissions.readAlerts],
  },
  {
    exact: true,
    id: 'queuedAlertsEntities',
    name: 'Queued Alerts by Entities',
    path: '/alerts/queued-alerts/entities',
    permissions: [permissions.readAlerts, permissions.readEntities],
  },
  {
    exact: true,
    id: 'queuedAlertsInstruments',
    name: 'Queued Alerts by Instruments',
    path: '/alerts/queued-alerts/instruments',
    permissions: [permissions.readAlerts, permissions.readTxnInstruments],
  },
  {
    exact: true,
    id: 'alertsCreate',
    name: 'Create Alert',
    path: routes.lumos.alertsCreate,
  },
  { exact: true, id: 'alertsId', name: 'Alert', path: routes.lumos.alertsId },
  {
    exact: true,
    id: 'alertsIdTab',
    name: 'Alert',
    path: routes.lumos.alertsIdTab,
  },
  {
    exact: true,
    id: 'alertPDF',
    name: 'Alert PDF',
    path: routes.lumos.pdfExportAlertsAlertId,
  },

  // External forms
  {
    exact: true,
    id: 'rfi',
    name: 'RFI',
    path: '/external-forms/rfi',
    permissions: [permissions.editExternalFormTemplates],
  },
  {
    exact: true,
    id: 'rfiRequests',
    name: 'RFI Requests',
    path: '/external-forms/rfi-requests',
    permissions: [permissions.editExternalFormTemplates],
  },
  {
    id: 'externalForms',
    name: 'External Forms',
    path: '/external-forms',
    anyPermissions: [permissions.editExternalFormTemplates],
  },

  // Cases
  {
    exact: true,
    id: 'myCases',
    name: 'My Cases',
    path: '/cases/my-cases',
    permissions: [permissions.readCases],
  },
  {
    exact: true,
    id: 'queuedCases',
    name: 'Queued Cases',
    path: '/cases/queued-cases',
    permissions: [permissions.readCases],
  },
  {
    exact: true,
    id: 'createCase',
    name: 'Create Case',
    path: '/cases/create-case',
    permissions: [permissions.createCases],
  },
  {
    id: 'adminCases',
    name: 'Admin Cases',
    path: '/cases/admin',
    permissions: [permissions.readCases, permissions.readAssignments],
  },
  {
    exact: true,
    id: 'cases',
    name: 'Cases',
    path: routes.lumos.cases,
    permissions: [permissions.readCases],
  },
  {
    exact: true,
    id: 'casesCreate',
    name: 'Create Case',
    path: routes.lumos.casesCreate,
    permissions: [permissions.readCases, permissions.createCases],
  },
  {
    exact: true,
    id: 'casesId',
    name: 'Case',
    path: routes.lumos.casesId,
    permissions: [permissions.readCases],
  },
  {
    exact: true,
    id: 'casesIdTab',
    name: 'Case',
    path: routes.lumos.casesIdTab,
  },
  {
    exact: true,
    id: 'pdfExportCasesId',
    name: 'Case PDF',
    path: routes.lumos.pdfExportCasesId,
    permissions: [permissions.readCases],
  },

  // Filings
  {
    exact: true,
    id: 'myFinCENSARs',
    name: 'My FinCEN SARs',
    path: '/filings/my-filings/fincen-sars',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'myFinCENCTRs',
    name: 'My FinCEN CTRs',
    path: '/filings/my-filings/fincen-ctrs',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'myFinCENDOEPs',
    name: 'My FinCEN DOEPs',
    path: '/filings/my-filings/fincen-doeps',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'myFinTRACSTRs',
    name: 'My FinTRAC STRs',
    path: '/filings/my-filings/fintrac-strs',
    permissions: [permissions.readFintrac],
  },
  {
    exact: true,
    id: 'myGoAML',
    name: 'My goAML Filings',
    path: '/filings/my-filings/go-aml',
    permissions: [permissions.readSars],
  },
  {
    exact: false,
    id: 'myFilings',
    name: 'My Filings',
    path: '/filings/my-filings',
    anyPermissions: [permissions.readSars, permissions.readFintrac],
  },
  {
    exact: true,
    id: 'queuedFinCENSARs',
    name: 'Queued FinCEN SARs',
    path: '/filings/queued-filings/fincen-sars',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'queuedFinCENCTRs',
    name: 'Queued FinCEN CTRs',
    path: '/filings/queued-filings/fincen-ctrs',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'queuedFinCENDOEPs',
    name: 'Queued FinCEN DOEPs',
    path: '/filings/queued-filings/fincen-doeps',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'queuedFinTRACSTRs',
    name: 'Queued FinTRAC STRs',
    path: '/filings/queued-filings/fintrac-strs',
    permissions: [permissions.readFintrac],
  },
  {
    exact: true,
    id: 'queuedGoAML',
    name: 'Queued goAML Filings',
    path: '/filings/queued-filings/go-aml',
    permissions: [permissions.readSars],
  },
  {
    id: 'queuedFilings',
    name: 'Queued Filings',
    path: '/filings/queued-filings',
    anyPermissions: [permissions.readSars, permissions.readFintrac],
  },
  {
    exact: true,
    id: 'adminFinCENSARs',
    name: 'Admin FinCEN SARs',
    path: '/filings/admin/fincen-sars',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'adminFinCENCTRs',
    name: 'Admin FinCEN CTRs',
    path: '/filings/admin/fincen-ctrs',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'adminFinCENDOEPs',
    name: 'Admin FinCEN DOEPs',
    path: '/filings/admin/fincen-doeps',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'adminFinTRACSTRs',
    name: 'Admin FinTRAC STRs',
    path: '/filings/admin/fintrac-strs',
    permissions: [permissions.readFintrac],
  },
  {
    exact: true,
    id: 'adminGoAML',
    name: 'Admin goAML Filings',
    path: '/filings/admin/go-aml',
    permissions: [permissions.readSars],
  },
  {
    id: 'adminFilings',
    name: 'Admin Filings',
    path: '/filings/admin',
    anyPermissions: [permissions.readSars, permissions.readFintrac],
    permissions: [permissions.readAssignments],
  },
  {
    exact: true,
    id: 'filingsSarDownload',
    name: 'Download FinCEN SAR',
    path: '/filings/sar/:id/download',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'filingsSarId',
    name: 'FinCEN SAR',
    path: routes.lumos.filingsSarId,
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'filingsSarIdTab',
    name: 'FinCEN SAR',
    path: '/filings/sar/:id/:tab',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'filingsGoAMLIdEdit',
    name: 'goAML Filing',
    path: '/filings/go-aml/:id/edit',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'filingsGoAMLIdEditTab',
    name: 'goAML Filing',
    path: '/filings/go-aml/:id/edit/:tab?',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'filingsGoAMLCreate',
    name: 'Create goAML Filing',
    path: '/filings/go-aml/create',
    permissions: [permissions.createSars],
  },
  {
    exact: true,
    id: 'filingsFintracStrId',
    name: 'FINTRAC STR',
    path: '/filings/fintrac/str/:id/:tab?',
    permissions: [permissions.readFintrac],
  },

  // Report Filings: FinCEN CTR
  {
    exact: true,
    id: 'filingsFincenCtrDownload',
    name: 'Download FinCEN CTR',
    path: '/filings/fincen-ctr/:id/download',
    permissions: [permissions.readSars],
  },
  {
    exact: true,
    id: 'filingsFincenCtrEdit',
    name: 'FinCEN CTR',
    path: '/filings/fincen-ctr/:id/:tab?',
  },

  // Report Filings: FinCEN DOEP
  {
    exact: true,
    id: 'filingsFincenDoepCreate',
    name: 'Create FinCEN DOEP',
    path: '/filings/doep/create',
    permissions: [permissions.createSars],
  },
  {
    exact: true,
    id: 'filingsFincenDoepId',
    name: 'FinCEN DOEP',
    path: '/filings/doep/:id/:tab?',
    permissions: [permissions.readSars],
  },

  // Filings configuration
  {
    id: 'finCENConfiguration',
    name: 'FinCEN SAR Settings',
    path: '/filings/configuration/fincen',
    permissions: [permissions.readSars],
    exact: false,
  },
  {
    id: 'finCENConfigurationTab',
    name: 'FinCEN SAR Settings',
    path: '/filings/configuration/fincen/:tab',
    permissions: [permissions.readSars],
  },
  {
    id: 'goAMLConfiguration',
    name: 'goAML STR Settings',
    path: '/filings/configuration/goaml',
    permissions: [permissions.readSars],
  },
  {
    id: 'finCENCtrConfiguration',
    name: 'FinCEN CTR Settings',
    path: '/filings/configuration/fincenctr',
    permissions: [permissions.readSars],
  },
  {
    id: 'fintracStrConfiguration',
    name: 'FINTRAC STR Settings',
    path: '/filings/configuration/fintrac-str',
    permissions: [permissions.readFintrac],
  },
  {
    id: 'fintracStrConfigurationTab',
    name: 'FINTRAC STR Settings',
    path: '/filings/configuration/fintrac-str/:tab',
    permissions: [permissions.readFintrac],
  },
  {
    id: 'filingsConfiguration',
    name: 'Report Filings Settings',
    path: '/filings/configuration',
    anyPermissions: [permissions.readSars, permissions.readFintrac],
    exact: false,
  },
  {
    exact: false,
    id: 'filings',
    name: 'Filings',
    path: routes.lumos.filings,
    anyPermissions: [permissions.readSars, permissions.readFintrac],
  },

  // AI
  {
    id: 'aiTaskEditPage',
    name: 'AI Agent Task',
    path: '/ai-agents/:agent_id/task/:task_id/edit',
    permissions: [permissions.editAiAgents, permissions.readAiAgents],
  },
  {
    id: 'aiTaskCreatePage',
    name: 'Create AI Agent Task',
    path: '/ai-agents/:agent_id/task/create',
    permissions: [permissions.editAiAgents, permissions.readAiAgents],
  },
  {
    exact: true,
    id: 'aiTask',
    name: 'AI Agent Task',
    path: '/ai-agents/:agent_id/task/:task_id',
    permissions: [permissions.editAiAgents, permissions.readAiAgents],
  },
  {
    id: 'aiAgentsId',
    name: 'Alert AI Agent Details',
    path: '/ai-agents/:agent_id',
    permissions: [permissions.readAiAgents],
  },
  {
    id: 'aiAgents',
    name: 'Alert AI Agents',
    path: '/ai/ai-agents',
    permissions: [permissions.readAiAgents],
  },
  {
    id: 'caseAiAgentsId',
    name: 'Case AI Agent Details',
    path: '/case-ai-agents/:agent_id',
    permissions: [permissions.readAiAgents],
  },
  {
    id: 'caseAiAgents',
    name: 'Case AI Agents',
    path: '/ai/case-ai-agents',
    permissions: [permissions.readAiAgents],
  },
  {
    id: 'filingAiAgentsId',
    name: 'Filing AI Agent Details',
    path: '/filing-ai-agents/:agent_id',
    permissions: [permissions.readAiAgents, permissions.readSars],
  },
  {
    id: 'filingAiAgents',
    name: 'Filing AI Agents',
    path: '/ai/filing-ai-agents',
    permissions: [permissions.readAiAgents, permissions.readSars],
  },
  {
    id: 'ai',
    name: 'AI',
    path: '/ai',
    permissions: [permissions.readAiAgents],
  },

  // Insights
  {
    id: 'reportingProgramEfficiency',
    name: 'Program Efficiency',
    path: '/dashboards/program-efficiency',
    permissions: [permissions.readInsights],
  },
  {
    id: 'reportingFraudLoss',
    name: 'Fraud Loss',
    path: '/dashboards/fraud-loss',
    permissions: [permissions.readInsights],
  },
  {
    id: 'reportingFileExports',
    name: 'File Exports',
    path: '/dashboards/file-exports',
    anyPermissions: [
      permissions.readCases,
      permissions.readAlerts,
      permissions.readSars,
      permissions.readAssignments,
    ],
  },

  {
    exact: true,
    id: 'dashboardsId',
    name: 'Dashboards',
    path: routes.lumos.dashboardsId,
    permissions: [permissions.readInsights],
  },
  {
    id: 'dashboards',
    name: 'Reporting',
    path: routes.lumos.dashboards,
    anyPermissions: [
      permissions.readAlerts,
      permissions.readAssignments,
      permissions.readCases,
      permissions.readInsights,
      permissions.readSars,
    ],
  },

  // Entities
  {
    exact: true,
    id: 'dataExplorerEntities',
    name: 'Entities',
    path: routes.lumos.dataExplorerEntities,
    permissions: [permissions.readEntities],
  },
  {
    exact: true,
    id: 'entitiesIdTab',
    name: 'Entity',
    path: routes.lumos.entitiesIdTab,
    permissions: [permissions.readEntities],
  },
  {
    id: 'entitiesId',
    name: 'Entity',
    path: routes.lumos.entitiesId,
    permissions: [permissions.readEntities],
  },

  // Instruments
  {
    exact: true,
    id: 'dataExplorerInstruments',
    name: 'Instruments',
    path: routes.lumos.dataExplorerInstruments,
    permissions: [permissions.readTxnInstruments],
  },
  {
    exact: true,
    id: 'instrumentsIdTab',
    name: 'Instrument',
    path: routes.lumos.instrumentsIdTab,
    permissions: [permissions.readTxnInstruments],
  },
  {
    id: 'instrumentsId',
    name: 'Instrument',
    path: routes.lumos.instrumentsId,
    permissions: [permissions.readTxnInstruments],
  },

  // Transactions
  {
    exact: true,
    id: 'dataExplorerTransactions',
    name: 'Transactions',
    path: routes.lumos.dataExplorerTransactions,
    permissions: [permissions.readEvents],
  },
  {
    exact: true,
    id: 'dataExplorerTransactionsId',
    name: 'Transaction',
    path: routes.lumos.dataExplorerTransactionsId,
    permissions: [permissions.readEvents],
  },

  // Actions
  {
    exact: true,
    id: 'dataExplorerActionEvents',
    name: 'Actions',
    path: routes.lumos.dataExplorerActionEvents,
    permissions: [permissions.readEvents],
  },
  {
    exact: true,
    id: 'dataExplorerActionEventsId',
    name: 'Actions',
    path: routes.lumos.dataExplorerActionEventsId,
    permissions: [permissions.readEvents],
  },

  // Upload file
  {
    exact: true,
    id: 'dataExplorerUploadFiles',
    name: 'Upload File',
    path: routes.lumos.dataExplorerUploadFile,
    permissions: [permissions.readDatafileUploads],
  },

  // Data explorer
  {
    id: 'dataExplorer',
    name: 'Data Explorer',
    path: routes.lumos.dataExplorer,
    anyPermissions: [
      permissions.readEntities,
      permissions.readTxnInstruments,
      permissions.readEvents,
      permissions.readDatafileUploads,
    ],
  },

  // Data Management Tab
  {
    exact: true,
    id: 'dataManagementFlatFileIngestion',
    name: 'Flat-File Ingestion',
    path: '/data-management/flat-file-ingestion',
    permissions: [permissions.readDDBConnections],
  },
  {
    exact: true,
    id: 'dataManagementDataMapping',
    name: 'Data Mapping',
    path: '/data-management/data-mapping/:id',
    permissions: [permissions.editDDBConnections],
  },
  {
    id: 'dataSettings',
    name: 'Data Settings',
    path: '/data-management/data-settings',
    permissions: [permissions.readCustomDataSettings],
  },
  {
    id: 'dataSettingsImportExport',
    name: 'Data Settings Import Export',
    path: '/data-management/export-import',
    permissions: [
      permissions.editCustomDataSettings,
      permissions.editDDBConnections,
    ],
  },
  {
    id: 'dataSettingsImport',
    name: 'Data Settings Import',
    path: '/data-management/export-import/import',
    permissions: [
      permissions.editCustomDataSettings,
      permissions.editDDBConnections,
    ],
  },
  {
    id: 'dataSettingsExport',
    name: 'Data Settings Export',
    path: '/data-management/export-import/export',
    permissions: [
      permissions.editCustomDataSettings,
      permissions.editDDBConnections,
    ],
  },
  {
    id: 'dataSettingsDownload',
    name: 'Download Data Settings',
    path: '/data-management/data-settings/download',
    permissions: [permissions.readCustomDataSettings],
  },
  {
    id: 'displaySettings',
    name: 'Display Settings',
    path: '/data-management/display-settings',
    anyPermissions: [
      permissions.editCustomDataSettings,
      permissions.readCensorshipConditions,
    ],
  },
  {
    id: 'tableSettings',
    name: 'Default Table Settings',
    path: '/data-management/display-settings/table-settings',
    permissions: [permissions.editCustomDataSettings],
  },
  {
    id: 'tableSettingsClassifier',
    name: 'Default Table Settings',
    path: '/data-management/display-settings/table-settings/:classifier',
    permissions: [permissions.editCustomDataSettings],
  },
  {
    id: 'summarySettings',
    name: 'Summary Settings',
    path: '/data-management/display-settings/summary-settings',
    permissions: [permissions.editCustomDataSettings],
  },
  {
    id: 'summarySettingsClassifier',
    name: 'Summary Settings',
    path: '/data-management/display-settings/summary-settings/:classifier',
    permissions: [permissions.editCustomDataSettings],
  },
  {
    id: 'watchlistSettings',
    name: 'Watchlist Settings',
    path: '/data-management/display-settings/watchlist-settings',
    permissions: [
      permissions.readCustomDataSettings,
      permissions.editCustomDataSettings,
    ],
  },
  {
    id: 'watchlistSettingsClassifier',
    name: 'Watchlist Settings',
    path: '/data-management/display-settings/watchlist-settings/:classifier',
    permissions: [
      permissions.readCustomDataSettings,
      permissions.editCustomDataSettings,
    ],
  },
  {
    id: 'networkAnalysisSettings',
    name: 'Network Analysis Settings',
    path: '/data-management/display-settings/network-analysis-settings',
    permissions: [permissions.editCustomDataSettings],
  },
  {
    id: 'networkAnalysisSettingsClassifier',
    name: 'Network Analysis Settings',
    path: '/data-management/display-settings/network-analysis-settings/:classifier',
    permissions: [permissions.editCustomDataSettings],
  },
  {
    id: 'typeClassification',
    name: 'Type Classification',
    path: '/data-management/type-classification',
    permissions: [permissions.editCustomDataSettings],
  },
  {
    id: 'relationships',
    name: 'Relationships',
    path: '/data-management/relationships',
    permissions: [permissions.editCustomDataSettings],
  },
  {
    id: 'relationshipConfigsId',
    name: 'Relationship',
    path: '/data-management/relationship-configs/:id',
  },
  {
    id: 'integrations',
    name: 'Integrations',
    path: '/data-management/integrations',
    anyPermissions: [
      permissions.readApiKeys,
      permissions.editStorage,
      permissions.readMcpSettings,
    ],
  },
  {
    id: 'apiKeys',
    name: 'API Keys',
    path: '/data-management/integrations/api-keys',
    permissions: [permissions.readApiKeys],
  },
  {
    id: 'externalAPIKeys',
    name: 'External API Keys',
    path: '/data-management/integrations/external-api-keys',
    permissions: [permissions.readApiKeys],
  },
  {
    id: 'googleDrive',
    name: 'Google Drive',
    path: '/data-management/integrations/google-drive',
    permissions: [permissions.editStorage],
  },
  {
    id: 'bankingIntegrations',
    name: 'Banking Integrations',
    path: '/data-management/integrations/banking-integrations',
    permissions: [permissions.editCoreBanking],
  },
  {
    id: 'mcpEnablement',
    name: 'MCP',
    path: '/data-management/integrations/mcp',
    permissions: [permissions.readMcpSettings],
  },
  {
    id: 'tags',
    name: 'Tags',
    path: '/data-management/tags',
  },
  {
    id: 'actionMapping',
    name: 'Action Mappings',
    path: '/data-management/action-mappings',
    permissions: [permissions.editCoreBanking],
  },
  {
    id: 'dataManagement',
    name: 'Data Management',
    path: '/data-management',
  },

  // Risk scores
  {
    exact: true,
    id: 'riskRatings',
    name: 'Risk Ratings',
    path: '/risk-ratings',
    permissions: [permissions.readRiskRatings],
  },
  {
    exact: true,
    id: 'riskRatingsId',
    name: 'Risk Model Details',
    path: '/risk-ratings/:id',
    permissions: [permissions.readRiskRatings],
  },
  {
    exact: true,
    id: 'riskRatingsIdTab',
    name: 'Risk Model Details',
    path: '/risk-ratings/:id/:tab?',
    permissions: [permissions.readRiskRatings],
  },

  // Entity Segments
  {
    exact: true,
    id: 'entityRiskSegmentsForm',
    name: 'Entity Risk Segment Form',
    path: '/risk-ratings/:id/entity-segments/form/:segmentId?',
    anyPermissions: [
      permissions.createEntitySegments,
      permissions.editEntitySegments,
      permissions.readEntitySegments,
    ],
  },
  // Rules
  {
    exact: true,
    id: 'rules',
    name: 'Rules',
    path: '/rules',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesSubRoutes',
    name: 'Rules',
    path: '/rules/status',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'liveRules',
    name: 'Live Rules',
    path: '/rules/status/live',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesValidating',
    name: 'Validating Rules',
    path: '/rules/status/validating',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesShadow',
    name: 'Shadow Rules',
    path: '/rules/status/shadow',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'flowRules',
    name: 'Nodes',
    path: '/rules/flows/flow-rules',
    permissions: [permissions.readRules],
  },
  // Rule Flow routes for better `Back to <tab?` navigation (So it actually shows the tab it goes back to. )
  {
    exact: true,
    id: 'ruleFlowsLive',
    name: 'Live Flows',
    path: '/rules/flows/live',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'ruleFlowsDrafts',
    name: 'Draft Flows',
    path: '/rules/flows/draft',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'ruleFlowsValidating',
    name: 'Validation Flows',
    path: '/rules/flows/validating',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'ruleFlows',
    name: 'Rule Flows',
    path: '/rules/flows/:tab?',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'flowDetail',
    name: 'Flow Detail',
    path: '/rules/flow/details/:id/:tab?',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'flowEdit',
    name: 'Edit Flow',
    path: '/rules/flow/edit/:id',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesSimpleCreate',
    name: 'Create Scenario Rule',
    path: '/rules/create/simple',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesCreateAdvancedTab',
    name: 'Create Dynamic Rule',
    path: '/rules/create/advanced/:tab?',
    permissions: [permissions.readRules],
  },
  {
    exact: false,
    id: 'rulesCreateAdvanced',
    name: 'Create Rule',
    path: '/rules/create/advanced',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesEditDynamicRecommendation',
    name: 'Edit Dynamic Rule Recommendation',
    path: '/rules/dynamic/recommendation',
    permissions: [permissions.createRules],
    requiredFeatureFlags: [FeatureFlag.RULE_RECOMMENDATIONS],
  },
  {
    exact: true,
    id: 'rulesEditRealTimeRecommendation',
    name: 'Edit Real-Time Rule Recommendation',
    path: '/rules/real-time/recommendation',
    permissions: [permissions.createRules],
    requiredFeatureFlags: [FeatureFlag.RULE_RECOMMENDATIONS],
  },
  {
    exact: true,
    id: 'subSecondRuleCreate',
    name: 'Create Real-Time Rule',
    path: '/rules/create/sub-second/',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesCreateRealTimeFlow',
    name: 'Create Real-Time Node',
    path: '/rules/create/flow-rule',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'variableLibrary',
    name: 'Variable Library',
    path: '/rules/variable-library',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesValidationId',
    name: 'Rule Validation',
    path: '/rules/validation/:id',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulesIdTab',
    name: 'Rule',
    path: '/rules/:id/:tab?',
    permissions: [permissions.readRules],
  },
  {
    id: 'rulesId',
    name: 'Rule',
    path: '/rules/:id',
    permissions: [permissions.readRules],
  },
  {
    exact: true,
    id: 'rulePDF',
    name: 'Rule PDF',
    path: '/pdf-export/rules/:ruleId',
    permissions: [permissions.readRules],
  },
  {
    id: 'rulesPdfExport',
    name: 'Rule PDF Export',
    path: '/pdf-export/rules/:ruleId',
  },

  // Lists
  {
    exact: true,
    id: 'lists',
    name: 'Lists',
    path: '/lists',
    anyPermissions: [permissions.readBlacklists, permissions.create314aScans],
  },
  // Lists: Matchlists
  {
    exact: true,
    id: 'matchlists',
    name: 'Matchlists',
    path: '/lists/matchlists',
    permissions: [permissions.readBlacklists],
  },
  {
    exact: true,
    id: 'matchlistsId',
    name: 'Matchlist',
    path: '/lists/matchlists/:id',
    permissions: [permissions.readBlacklists],
  },
  {
    exact: true,
    id: 'matchlistsIdTab',
    name: 'Matchlist',
    path: '/lists/matchlists/:id/:tab?',
    permissions: [permissions.readBlacklists],
  },
  // Lists: 314(a)
  {
    exact: true,
    id: 'lists314a',
    name: '314(a)',
    path: '/lists/314a',
    permissions: [permissions.create314aScans],
  },
  // Lists: Adhoc Screening
  {
    exact: true,
    id: 'listsWatchlist',
    name: 'Watchlist',
    path: '/lists/watchlist',
  },

  // Notifications
  {
    exact: true,
    id: 'notifications',
    name: 'Notifications',
    path: routes.lumos.notifications,
  },

  // Org Management
  {
    exact: true,
    id: 'orgCreate',
    name: 'Org Features',
    path: '/org-management/create',
    permissions: [permissions.editAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'orgIDTab',
    name: 'Org Details',
    path: '/org-management/:id/:tab',
    permissions: [permissions.editAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'orgID',
    name: 'Org Details',
    path: '/org-management/:id',
    permissions: [permissions.editAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'orgManagement',
    name: 'Org Management',
    path: '/org-management',
    permissions: [permissions.editAdminDashboard, permissions.adminAPI],
  },

  // AI admin
  {
    id: 'aiAdmin',
    name: 'AI Admin',
    path: '/ai-admin',
    permissions: [permissions.editAiAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'aiAdminAgents',
    name: 'AI Admin Agents',
    path: '/ai-admin/agents',
    permissions: [permissions.editAiAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'aiAdminTracking',
    name: 'AI Admin Tracking',
    path: '/ai-admin/tracking',
    permissions: [permissions.editAiAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'aiAdminStats',
    name: 'AI Admin Stats',
    path: '/ai-admin/stats',
    permissions: [permissions.editAiAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'aiAdminPrompts',
    name: 'AI Admin Prompts',
    path: '/ai-admin/prompts',
    permissions: [permissions.editAiAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'aiAdminExperimentation',
    name: 'AI Admin Experimentation',
    path: '/ai-admin/experimentation',
    permissions: [permissions.editAiAdminDashboard, permissions.adminAPI],
  },

  // Rules admin
  {
    id: 'rulesAdminRiskRatings',
    name: 'Rules Admin Risk Ratings',
    path: '/rules-admin/risk-ratings',
    permissions: [permissions.editRulesAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'rulesAdminAlerts',
    name: 'Rules Admin Alerts',
    path: '/rules-admin/alerts',
    permissions: [permissions.editRulesAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'rulesAdminMatchlists',
    name: 'Rules Admin Matchlists',
    path: '/rules-admin/matchlists',
    permissions: [permissions.editRulesAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'rulesAdminRuleRecs',
    name: 'Rules Admin Rule Recs',
    path: '/rules-admin/rule-recs',
    permissions: [permissions.editRulesAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'rulesAdminAIRecommendations',
    name: 'AI Rule Recommendations',
    path: '/rules-admin/ai-recommendations',
    permissions: [permissions.editRulesAdminDashboard, permissions.adminAPI],
  },
  {
    id: 'rulesAdmin',
    name: 'Rules Admin',
    path: '/rules-admin',
    permissions: [permissions.editRulesAdminDashboard, permissions.adminAPI],
  },

  // User management
  {
    id: 'agentsId',
    name: 'Agent',
    path: routes.lumos.agentsId,
  },
  {
    exact: true,
    id: 'agentsIdTab',
    name: 'Agent',
    path: routes.lumos.agentsIdTab,
  },
  {
    id: 'agents',
    name: 'Agents',
    path: '/user-management/agents',
    permissions: [permissions.readAgents],
  },
  {
    id: 'schedules',
    name: 'Schedules',
    path: '/user-management/schedules',
    permissions: [permissions.readTeams],
  },
  {
    id: 'scheduleId',
    name: 'Schedule',
    path: '/schedules/:id',
    permissions: [permissions.readTeams],
  },
  {
    id: 'teamsId',
    name: 'Team',
    path: routes.lumos.teamsId,
    permissions: [permissions.readTeams],
  },
  {
    exact: true,
    id: 'teamsIdTab',
    name: 'Team',
    path: routes.lumos.teamsIdTab,
    permissions: [permissions.readTeams],
  },
  {
    id: 'teams',
    name: 'Teams',
    path: '/user-management/teams',
    permissions: [permissions.readTeams],
  },
  {
    id: 'userManagement',
    name: 'User Management',
    path: '/user-management',
    anyPermissions: [permissions.readAgents, permissions.readTeams],
  },

  // Workflows
  // todo: rename to 'alertQueues' after old one is deleted, need DB migration
  {
    exact: true,
    id: 'workflowAlertQueues',
    name: 'Alert Queues',
    path: '/workflows/queues/alerts',
    permissions: [permissions.readAlerts, permissions.editQueues],
  },
  {
    exact: true,
    id: 'alertQueuesId',
    name: 'Alert Queue',
    path: '/queues/alerts/:id',
    permissions: [permissions.readAlerts, permissions.editQueues],
  },
  {
    exact: true,
    id: 'caseQueues',
    name: 'Case Queues',
    path: '/workflows/queues/cases',
    permissions: [permissions.readCases, permissions.editQueues],
  },
  {
    exact: true,
    id: 'caseQueuesId',
    name: 'Case Queue',
    path: '/queues/cases/:id',
    permissions: [permissions.readCases, permissions.editQueues],
  },
  {
    exact: true,
    id: 'sarQueues',
    name: 'Filing Queues',
    path: '/workflows/queues/filings',
    permissions: [permissions.readSars, permissions.editQueues],
  },
  {
    exact: true,
    id: 'sarQueuesId',
    name: 'Filing Queue',
    path: '/queues/filings/:id',
    permissions: [permissions.readSars, permissions.editQueues],
  },
  {
    exact: true,
    id: 'queues',
    name: 'Queues',
    path: '/workflows/queues',
    permissions: [permissions.editQueues],
  },
  {
    exact: true,
    id: 'alertWorkflowButtonCreate',
    name: 'Create Alert Workflow Button',
    path: '/workflows/buttons/alerts/templates/:templateId',
    permissions: [permissions.readAlerts, permissions.createWebhooks],
  },
  {
    exact: true,
    id: 'alertWorkflowButtonTemplates',
    name: 'Alert Workflow Button Templates',
    path: '/workflows/buttons/alerts/templates',
    permissions: [permissions.readAlerts, permissions.createWebhooks],
  },
  {
    exact: true,
    id: 'alertWorkflowButton',
    name: 'Alert Workflow Button',
    path: '/workflows/buttons/alerts/:id',
    permissions: [permissions.readAlerts, permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'caseWorkflowButtonCreate',
    name: 'Create Case Workflow Button',
    path: '/workflows/buttons/cases/templates/:templateId',
    permissions: [permissions.readCases, permissions.createWebhooks],
  },
  {
    exact: true,
    id: 'caseWorkflowButton',
    name: 'Case Workflow Button',
    path: '/workflows/buttons/cases/:id',
    permissions: [permissions.readCases, permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'alertWorkflowButtons',
    name: 'Alert Workflow Buttons',
    path: '/workflows/buttons/alerts',
    permissions: [permissions.readAlerts, permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'caseWorkflowButtons',
    name: 'Case Workflow Buttons',
    path: '/workflows/buttons/cases',
    permissions: [permissions.readCases, permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'workflowButtons',
    name: 'Workflow Buttons',
    path: '/workflows/buttons',
    permissions: [permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'alertInvestigationChecklists',
    name: 'Alert Investigation Checklists',
    path: '/workflows/investigation-checklists/alerts',
    permissions: [permissions.readAlerts, permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'caseInvestigationChecklists',
    name: 'Case Investigation Checklists',
    path: '/workflows/investigation-checklists/cases',
    permissions: [permissions.readCases, permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'investigationChecklistsId',
    name: 'Investigation Checklist',
    path: '/investigation-checklists/:id',
    permissions: [permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'investigationChecklists',
    name: 'Investigation Checklists',
    path: '/workflows/investigation-checklists',
    permissions: [permissions.readWebhooks],
  },
  {
    id: 'alertNarrativeTemplates',
    name: 'Alert Narrative Templates',
    path: '/workflows/narrative-templates/alerts',
    permissions: [permissions.readAlerts, permissions.readWebhooks],
  },
  {
    id: 'caseNarrativeTemplates',
    name: 'Case Narrative Templates',
    path: '/workflows/narrative-templates/cases',
    permissions: [permissions.readCases, permissions.readWebhooks],
  },
  {
    id: 'narrativeTemplates',
    name: 'Narrative Templates',
    path: '/workflows/narrative-templates',
    permissions: [permissions.readWebhooks],
  },
  {
    id: 'webhooks',
    name: 'Webhooks',
    path: '/workflows/webhooks',
    permissions: [permissions.readWebhooks],
  },
  {
    id: 'webhooksId',
    name: 'Webhook',
    path: routes.lumos.webhooksId,
    permissions: [permissions.readWebhooks],
  },
  {
    id: 'deadlines',
    name: 'Deadlines',
    path: '/workflows/deadlines',
    permissions: [permissions.readDeadlinesConfig],
  },
  {
    id: 'calendars',
    name: 'Calendars',
    path: '/workflows/calendars',
    exact: true,
    permissions: [permissions.manageCalendars],
    requiredFeatureFlags: [FeatureFlag.BUSINESS_DAY_CALENDARS],
  },
  {
    id: 'calendarsCreate',
    name: 'Create Calendar',
    path: '/workflows/calendars/create',
    exact: true,
    permissions: [permissions.manageCalendars],
    requiredFeatureFlags: [FeatureFlag.BUSINESS_DAY_CALENDARS],
  },
  {
    id: 'calendarsId',
    name: 'Calendar',
    path: '/workflows/calendars/:id',
    permissions: [permissions.manageCalendars],
    requiredFeatureFlags: [FeatureFlag.BUSINESS_DAY_CALENDARS],
  },
  {
    exact: true,
    id: 'dispositions',
    name: 'Dispositions',
    path: '/workflows/dispositions',
    permissions: [permissions.readWebhooks],
  },
  {
    exact: true,
    id: 'quality',
    name: 'Quality',
    path: '/workflows/quality',
    permissions: [permissions.readQuality],
  },
  {
    id: 'workflowVisualizer',
    name: 'Workflow Visualizer',
    path: '/workflows/visualizer',
    requiredFeatureFlags: [FeatureFlag.WORKFLOW_VISUALIZER],
    anyPermissions: [
      permissions.readAlerts,
      permissions.readCases,
      permissions.readSars,
      permissions.readWebhooks,
    ],
  },
  {
    id: 'workflows',
    name: 'Workflows',
    path: '/workflows',
    anyPermissions: [
      permissions.readAssignments,
      permissions.readWebhooks,
      permissions.readDeadlinesConfig,
      permissions.manageCalendars,
    ],
  },

  // Quality Cycles
  {
    id: 'qualityId',
    name: 'Quality Cycles',
    path: '/quality/:id',
    permissions: [permissions.readQuality],
  },

  // Misc
  { exact: true, id: 'home', name: 'Home', path: routes.lumos.home },
  {
    exact: true,
    id: 'agentLogin',
    name: 'Login',
    path: routes.lumos.agentLogin,
  },
  {
    exact: true,
    id: 'emailLogin',
    name: 'Login',
    path: routes.lumos.emailLogin,
  },
  {
    exact: true,
    id: 'mcpConsent',
    name: 'MCP Consent',
    path: '/mcp/consent',
  },
  {
    exact: true,
    id: 'externalIdDetected',
    name: 'Invalid ID',
    path: routes.lumos.externalIdDetected,
  },
  {
    exact: true,
    id: 'error',
    name: 'Error',
    path: routes.lumos.error,
  },
  {
    id: 'fourOhFour',
    name: 'Not found',
    path: '/404',
  },
] as const satisfies DeepReadonly<Route[]>;

type RouteID = (typeof ROUTES)[number]['id'];
export const ROUTES_MAP: Record<RouteID, Route> = ROUTES.reduce(
  (acc, i) => ({
    ...acc,
    [i.id]: i,
  }),
  {} as Record<RouteID, Route>,
);

export const NOT_ALLOWED_BACK_ROUTES = {
  [ROUTES_MAP.agentLogin.path]: true,
  [ROUTES_MAP.emailLogin.path]: true,
  [ROUTES_MAP.home.path]: true,
  [ROUTES_MAP.error.path]: true,
  [ROUTES_MAP.fourOhFour.path]: true,
  [ROUTES_MAP.externalIdDetected.path]: true,
};

export const getRouteName = (route: string): string => {
  const found = ROUTES.find((i) => matchPath(route, i));
  if (!found) {
    consoleWarn('Route name not found!', route);
    return '';
  }
  return found.name;
};

export const HOME_ROUTES = [
  ROUTES_MAP.myAlerts,
  ROUTES_MAP.alertAdmin,
  ROUTES_MAP.dashboards,
  ROUTES_MAP.workflowAlertQueues,
];

export const NO_AUTH_ROUTES = new Set([
  ROUTES_MAP.agentLogin.path,
  ROUTES_MAP.emailLogin.path,
]);

export default routes;
